import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Check } from 'lucide-react';
import { getSubtleTagStyle } from '@/components/ui/column-utilities';
import { useMatterLabels } from '@/lib/useMatterLabels';

interface SimpleColorPickerGridProps {
  selectedColor: string;
  onColorSelect: (color: string) => void;
  colors: string[];
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  compact?: boolean;
  /**
   * When true, swatches render with the same muted tint used to display tags
   * elsewhere in the app (see `getSubtleTagStyle`) instead of the raw color.
   * Use for tag pickers so the preview matches the rendered tag; leave false
   * when the picked color is applied literally (e.g. bundle text/background).
   */
  subtle?: boolean;
}

export const SimpleColorPickerGrid: React.FC<SimpleColorPickerGridProps> = ({
  selectedColor,
  onColorSelect,
  colors,
  isOpen: controlledIsOpen,
  onOpenChange,
  compact = false,
  subtle = false,
}) => {
  const label = useMatterLabels();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [position, setPosition] = useState<'left' | 'right'>('left');
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Use controlled or uncontrolled state based on props
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = (open: boolean) => {
    setInternalIsOpen(open);
    if (onOpenChange) {
      onOpenChange(open);
    }
  };
  
  // Determine position to prevent cut-off
  useLayoutEffect(() => {
    if (isOpen && buttonRef.current && pickerRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const pickerWidth = 240; // Width of the picker in pixels
      
      // Find the nearest scrollable parent to check for table boundaries
      let parent = buttonRef.current.parentElement;
      let tableContainer = null;
      
      while (parent) {
        if (parent.classList.contains('overflow-auto') || 
            window.getComputedStyle(parent).overflowX === 'auto' || 
            window.getComputedStyle(parent).overflowY === 'auto') {
          tableContainer = parent;
          break;
        }
        parent = parent.parentElement;
      }
      
      // Check if opening to the left would cause overflow
      const rightEdge = buttonRect.left + pickerWidth;
      const windowWidth = window.innerWidth;
      
      // Check table container boundaries if found
      if (tableContainer) {
        const tableRect = tableContainer.getBoundingClientRect();
        const tableRightEdge = tableRect.right;
        
        // If the picker would go off the right edge of the table, position it to the right
        if (rightEdge > tableRightEdge) {
          setPosition('right');
          return;
        }
      }
      
      // If no table constraints or within table bounds, check window bounds
      if (rightEdge > windowWidth) {
        setPosition('right');
      } else {
        setPosition('left');
      }
    }
  }, [isOpen]);
  
  // Close when clicking outside the picker but inside the dialog
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Close when clicking outside the color picker but inside the dialog
      if (
        pickerRef.current && 
        !pickerRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      // Use a timeout to prevent the same click from immediately closing
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 10);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`${compact ? 'w-7 h-7 rounded-sm' : 'w-10 h-10 rounded-md'} border border-gray-300 flex items-center justify-center p-0 focus:outline-hidden focus:ring-2 focus:ring-blue-500 hover:shadow-md transition-shadow`}
        style={{
          backgroundColor: subtle
            ? getSubtleTagStyle(selectedColor || '#CCCCCC').backgroundColor
            : selectedColor || '#CCCCCC',
        }}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={label('select-colour')}
      >
        {/* Empty, just for color swatch */}
      </button>
      
      {isOpen && (
        <div
          ref={pickerRef}
          className={`absolute z-50 bg-white rounded-md shadow-xl p-3 border border-gray-200 mt-2 ${position === 'right' ? 'right-0' : 'left-0'}`}
          style={{
            maxHeight: compact ? '180px' : '240px',
            width: compact ? '200px' : '260px',
            overflowY: 'auto',
            transform: 'translateZ(0)', // Force hardware acceleration for smoother rendering
          }}
        >
          <div className={`grid grid-cols-6 ${compact ? 'gap-1' : 'gap-2'} py-1`}>
            {colors.map((color) => {
              const subtleStyle = getSubtleTagStyle(color);
              return (
                <button
                  key={color}
                  type="button"
                  className={`${compact ? 'w-6 h-6 rounded-sm' : 'w-8 h-8 rounded-md'} border border-gray-300 flex items-center justify-center focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-all`}
                  style={{ backgroundColor: subtle ? subtleStyle.backgroundColor : color }}
                  onClick={() => {
                    onColorSelect(color);
                    setIsOpen(false);
                  }}
                  aria-label={label('select-colour-value', { colour: color })}
                >
                  {selectedColor.toLowerCase() === color.toLowerCase() && (
                    subtle ? (
                      <Check className={`${compact ? 'h-3 w-3' : 'h-4 w-4'}`} style={{ color: subtleStyle.color }} />
                    ) : (
                      <Check className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-white drop-shadow-[0_0px_1px_rgba(0,0,0,0.8)]`} />
                    )
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};